<template>
  <div
    v-if="vehicleDetails.length"
    class="vehicle-details-list"
  >
    <ul class="vehicle-details-list__details">
      <li
        v-for="(detail, index) of vehicleDetails"
        :key="`${detail.handle}-${index}`"
        class="vehicle-details-list__detail"
      >
        <span
          class="text-body-s-bold"
          v-text="detail.title"
        />

        <span
          class="vehicle-details-list__value text-body-s-regular"
          v-text="detail.value"
        />

        <button
          v-if="detail.motHandle"
          class="vehicle-details-list__refresh"
          :class="{ [$class('active')]: refreshing }"
          type="button"
          @click="handleMotRefresh"
        >
          <icon-refresh aria-hidden="true" />

          <span
            class="text-body-xs-underline"
            v-text="$string('customers.garage.refresh')"
          />
        </button>
      </li>
    </ul>

    <transition-height v-if="product">
      <product-compatibility
        v-if="activeVehicle && showCompatibility"
        class="vehicle-details-list__compatibility"
        :product="product"
      />
    </transition-height>
  </div>
</template>

<script>

/**
 * Vue: Vehicle details list (vehicle-details-list)
 * -----------------------------------------------------------------------------
 * Component to display vehicle details list in product card or account garage.
 *
 * @param {Boolean|Object} product - Compatibility product.
 * @param {Boolean|import("~stores/garage").Vehicle} vehicle - Vehicle.
 *
 * @emits refreshMot - Emit refresh MOT event.
 *
 */
import { mapState } from 'vuex'

import ProductCompatibility from '~async/product-compatibility/product-compatibility'
import TransitionHeight from '~async/transition-height/transition-height'

import IconRefresh from '~icons/general/refresh.svg'

import {
  formatVehicleDetailValue,
  formatYears,
  isYearHandle,
  validateVehicle,
} from '~/helpers/garage'

export default {
  name: 'VehicleDetailsList',

  components: {
    IconRefresh,
    ProductCompatibility,
    TransitionHeight,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
    vehicle: {
      type: [Boolean, Object],
      default: false,
      validator: validateVehicle,
    },
  },

  emits: {
    'refresh-mot': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      refreshing: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeVehicle: (state) => state.garage.active,
      showCompatibility: (state) => state.garage.showCompatibility,
    }),

    /**
     * Compute details based on metafield and theme setting order.
     * @return {Array}
     */
    vehicleDetails() {
      if (!this.vehicle && !this.product?.taxonomy) {
        return []
      }

      const array = Object.values(this.$variable('vehicleFinder.vehicleDetails'))

      const vehicleDetails = this.product
        ? array.map(this.getProductVehicleDetails)
        : array.map(this.getGarageVehicleDetails)

      const sortVariable = this.product
        ? this.$variable('vehicleFinder.productVehicleDetails')
        : this.$variable('vehicleFinder.accountGarageVehicleDetails')

      return vehicleDetails
        .filter(Boolean)
        .sort((a, b) => {
          const indexA = sortVariable.indexOf(a.handle)
          const indexB = sortVariable.indexOf(b.handle)

          return indexA === -1 || indexB === -1
            ? a.handle.localeCompare(b.handle)
            : indexA - indexB
        })
    },
  },

  watch: {

    /**
     * Watch MOT updated number to reset refresh.
     */
    'vehicle.motUpdated'() {
      this.refreshing = false
    },
  },

  methods: {

    /**
     * Get product vehicle details.
     * - Returns multiple values for each detail.
     * @param {Object} vehicleDetail - Vehicle detail settings object.
     * @returns {Boolean|Object}
     */
    getProductVehicleDetails(vehicleDetail) {
      if (!vehicleDetail.show.product) {
        return false
      }

      const detail = this.product.taxonomy[vehicleDetail.key]

      if (!detail) {
        return false
      }

      let value = detail.length > 1
        ? this.$string('compatibility.types_covered', { replace: { count: detail.length } })
        : detail[0]

      if (isYearHandle(vehicleDetail.handle)) {
        value = formatYears(detail)
      }

      return {
        handle: vehicleDetail.handle,
        title: vehicleDetail.title,
        value,
      }
    },

    /**
     * Get garage vehicle details.
     * - Returns single value for each detail.
     * @param {Object} vehicleDetail - Vehicle detail settings object.
     * @returns {Boolean|Object}
     */
    getGarageVehicleDetails(vehicleDetail) {
      if (!vehicleDetail.show.accountGarage) {
        return false
      }

      let value = this.vehicle.details[vehicleDetail.handle]

      if (!value || !value?.length) {
        value = this.vehicle.user[vehicleDetail.handle]
      }

      if (!value || !value?.length) {
        return false
      }

      const motHandle = vehicleDetail.handle === this.$variable('vehicleFinder.handles.mot')

      value = motHandle
        ? this.$formatDate('date_formats.mot_due', new Date(value))
        : formatVehicleDetailValue(value, vehicleDetail.handle)

      return {
        handle: vehicleDetail.handle,
        motHandle,
        title: vehicleDetail.title,
        value,
      }
    },

    /**
     * Handle MOT refresh.
     */
    handleMotRefresh() {
      this.refreshing = true
      this.$emit('refresh-mot', this.vehicle)
    },
  },
}
</script>

<style lang="scss">
@import './vehicle-details-list';
</style>
